import {
    calculateDistance,
    getPointWithPointVecteurAndDistance,
    getOrthogonalProjection,
    makeVector,
    normalizeVector,
    productScalare,
    projectVector, computeAngleToRotate
} from "./TransformUtils";
import {
    getCentralPitActual, getCentralPitMaloc,
    getDistalPointActual,
    getIdTooth, getMesialPointActual,
    getRotationRootActual,
    getRotationRootMaloc, getVecArchTemplateNormal, getVecArchTemplateTangent,
    getVecRidgeActual, getVecRootActual, getVecTangentToFacialActual,
    getVecVestibularLingualActual,
} from "../../Mesh/Query/ToothQueryService";
import { updateAllMovementsDatas } from "../../Mesh/Movement/ToothMovementTracker";
import { addToSessionMovementHistory } from "../../Mesh/Handler/ToothSaveHandler";
import * as THREE from 'three';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { Store } from 'Application/Store/Store'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import matrixUpdateBatcher from '../Utils/MatrixUpdateBatcher';

// Vector3 réutilisables pour réduire les allocations
const _tempVector1 = new THREE.Vector3();
const _tempVector2 = new THREE.Vector3();
const _tempVector3 = new THREE.Vector3();
const _axisX = new THREE.Vector3(1, 0, 0);
const _axisY = new THREE.Vector3(0, 1, 0);
const _axisZ = new THREE.Vector3(0, 0, 1);

let onMovementAppliedCallback = null;

export const setOnMovementAppliedCallback = (callback) => {
    onMovementAppliedCallback = callback;
};

export async function applyTranslationOrRotation(tooth, type, value) {
    if (!tooth) {
        return
    }

    const ancienMandiAdjustActive = Store.getState().ViewportReducer.mandiAdjustActive;
    const mandiGroupGlobal = threeEngine.getMandiGroup();
    if (!mandiGroupGlobal) return;
    const tempGroup = mandiGroupGlobal.clone(true);
    tempGroup.name = "mandiGroupTemp"

    try {

        if (ancienMandiAdjustActive) {
            const scene = threeEngine.getScene();
            if (scene) {
                scene.add(tempGroup);
            }
            mandiGroupGlobal.visible = false;
            mandiGroupGlobal.position.set(0, 0, 0);
        }

        switch (type) {
            case 'extrusionIntrusion':
                translateToothX(value, tooth);
                break;
            case 'lingualVestibular':
                translateToothY(value, tooth);
                break;
            case 'distalMesial':
                translateToothZ(value, tooth);
                break;
            case 'tip':
                rotateTip(value, tooth, Store.getState().ToothReducer.selectedPointTip);
                break;
            case 'torque':
                rotateTorque(value, tooth, Store.getState().ToothReducer.selectedPointTorque);
                break;
            case 'rotation':
                rotateRotation(value, tooth, Store.getState().ToothReducer.selectedPointRotation);
                break;
            default:
                break;
        }

        if (tooth.isStepInit) {
            tooth.mouvements = tooth.mouvements.slice(0, 1);
            tooth.movementActual = 0;
        } else {
            tooth.mouvements = tooth.mouvements.slice(0, tooth.movementActual + 1);
        }
        const selectedTooth = meshSelectionStore.getClickedTooth();
        if (selectedTooth) {
            tooth.mouvements.push(selectedTooth.pivot.matrix.clone());
        }

        tooth.isStepInit = false;
        tooth.movementActual++
    } finally {
        if (ancienMandiAdjustActive) {
            const vector = Store.getState().ViewportReducer.mandiAdjust;
            mandiGroupGlobal.position.set(vector.x, vector.y, vector.z);
            const scene = threeEngine.getScene();
            if (scene) {
                scene.remove(tempGroup);
            }
            mandiGroupGlobal.visible = true;
        }
    }

    await updateAllMovementsDatas(tooth);

    const toothId = getIdTooth(tooth);
    addToSessionMovementHistory(toothId, type, value);

    if (typeof onMovementAppliedCallback === 'function') {
        onMovementAppliedCallback(tooth, type, value);
    }

}

/**
 * Calcule la distance de déplacement en fonction du type de mouvement.
 */
function getAddDistance(tooth, type, value) {
    if (!tooth) {
        console.error("Tooth non défini");
        return null;
    }

    let vectorRef, vecActual, origin;

    switch (type) {
        case 'vestibularLingual':
            vectorRef = projectVector([0, 0, 1], getVecVestibularLingualActual(tooth));
            vecActual = getVecVestibularLingualActual(tooth);
            origin = getOrthogonalProjection(getRotationRootMaloc(tooth), getRotationRootActual(tooth), vectorRef);
            break;
        case 'distalMesial':
            vectorRef = projectVector([0, 0, 1], getVecRidgeActual(tooth));
            vecActual = getVecRidgeActual(tooth);
            origin = getOrthogonalProjection(getRotationRootMaloc(tooth), getRotationRootActual(tooth), vectorRef);
            break;
        case 'extrusionIntrusion':
            const vecRoot = getVecRootActual(tooth);
            const originMaloc = getCentralPitMaloc(tooth);
            const vecOriginToActual = makeVector(originMaloc, getCentralPitActual(tooth));

            // Remplace getProjection() en projetant directement sur l'axe [0,0,1]
            const coefficient = productScalare(vecOriginToActual, [0, 0, 1]) / productScalare([0, 0, 1], [0, 0, 1]);
            const originExtrusion = [
                originMaloc[0] + 0,
                originMaloc[1] + 0,
                originMaloc[2] + coefficient
            ];

            const quotient = productScalare([0, 0, value], [0, 0, 1]) / productScalare(vecRoot, [0, 0, 1]);
            const projPrime = vecRoot.map((component, i) => originExtrusion[i] + quotient * component);

            return calculateDistance(originExtrusion, projPrime);
        default:
            return null;
    }


    let VUni = normalizeVector(vectorRef);
    let pointProjete = getPointWithPointVecteurAndDistance(origin, VUni, -value, true);
    let originToProjete = makeVector(origin, pointProjete);

    let quotient = productScalare(originToProjete, VUni) / productScalare(vecActual, VUni);
    let projPrime = getPointWithPointVecteurAndDistance(origin, vecActual, quotient, true);

    return calculateDistance(origin, projPrime);
}

/**
 * Effectue une translation d'une dent sur un axe donné.
 */
function translateTooth(selectedTooth, axis, value, type = null) {
    if (!selectedTooth) return;

    let move = value;

    // Gestion des translations complexes (Y et Z)
    if (type) {
        move = getAddDistance(selectedTooth, type, value);
        //console.log(move)
        if (move === null) return;
        if (value > 0) move = -move;

        if (type === "extrusionIntrusion") {
            move = -move
        }

        // Inversion pour les dents spécifiques
        let idTooth = getIdTooth(selectedTooth);
        if (type === "distalMesial" && ((idTooth > 10 && idTooth < 19) || (idTooth >= 30 && idTooth < 39))) {
            move = -move;
        }
    }


    //console.log(`Ajout : ${move}`);

    selectedTooth.pivot.translateOnAxis(axis, move);
    selectedTooth.pivot.updateMatrix();
    matrixUpdateBatcher.add(selectedTooth.pivot);


    //console.log(`Déplacement sur l'axe ${axis.x}, ${axis.y}, ${axis.z} : ${value}`);
}

// Fonctions spécifiques basées sur translateTooth - utilisent les Vector3 réutilisables
export const translateToothX = (value, selectedTooth) => translateTooth(selectedTooth, _axisX, value, "extrusionIntrusion");
export const translateToothY = (value, selectedTooth) => translateTooth(selectedTooth, _axisY, value, "vestibularLingual");
export const translateToothZ = (value, selectedTooth) => translateTooth(selectedTooth, _axisZ, value, "distalMesial");

/**
 * Effectue une rotation d'une dent sur un axe donné.
 */

export const rotateRotation = (angle, tooth, namePoint) => {
    if (!tooth) return;

    let rad = angle * Math.PI / 180;
    // Utiliser le Vector3 réutilisable pour l'axe X
    let worldRotationPoint;

    console.log(namePoint)
    switch (namePoint) {
        case 'Distal':

            let distalPoint = getDistalPointActual(tooth)
            _tempVector1.set(distalPoint[0], distalPoint[1], distalPoint[2]);
            worldRotationPoint = _tempVector1;

            break;
        case 'Mesial':
            let mesialPoint = getMesialPointActual(tooth)
            _tempVector2.set(mesialPoint[0], mesialPoint[1], mesialPoint[2]);
            worldRotationPoint = _tempVector2;

            break;

        default: {
            tooth.pivot.rotateOnAxis(_axisX, rad);
            tooth.pivot.updateMatrix();
            matrixUpdateBatcher.add(tooth.pivot);
            return;
        }



    }

    tooth.updateWorldPosition();

    // Déplacement du pivot sur le point de rotation
    tooth.pivot.position.copy(worldRotationPoint);
    tooth.offsetRefObj();

    tooth.pivot.rotateOnAxis(_axisX, rad);
    tooth.pivot.updateMatrix();
    matrixUpdateBatcher.add(tooth.pivot);

    // Mettre le pivot de nouveau sur le barycentre
    tooth.baryCenter.getWorldPosition(_tempVector3);
    tooth.updateWorldPosition();

    tooth.pivot.position.copy(_tempVector3);
    tooth.offsetRefObj();
    matrixUpdateBatcher.add(tooth.pivot);



};

export const rotateTorque = (angle, tooth, namePoint) => {
    let idTooth = getIdTooth(tooth)
    if (((idTooth > 30 && idTooth < 39) || (idTooth >= 40 && idTooth < 49))) {
        angle = -angle;
    }
    angle = computeAngleToRotate(getVecRidgeActual(tooth), getVecArchTemplateTangent(tooth), getVecTangentToFacialActual(tooth), angle);
    //console.log("Angle rajouté " + angle)
    rotateToothOnTipOrTorque(tooth, getVecRidgeActual(tooth), angle, namePoint);
};



export const rotateTip = (angle, tooth, namePoint) => {
    let idTooth = getIdTooth(tooth)
    if (((idTooth > 10 && idTooth < 19) || (idTooth >= 30 && idTooth < 39))) {
        angle = -angle;
    }

    angle = computeAngleToRotate(getVecVestibularLingualActual(tooth), getVecArchTemplateNormal(tooth), getVecRootActual(tooth), angle)
    //console.log("Angle rajouté " + angle)
    rotateToothOnTipOrTorque(tooth, getVecVestibularLingualActual(tooth), angle, namePoint);
};

/**
 * Effectue une rotation autour d'un axe donné.
 */
function rotateToothOnTipOrTorque(tooth, rotationAxis, angle, namePoint) {
    if (!tooth || !rotationAxis) {
        console.error("Paramètres invalides", { tooth, rotationAxis });
        return;
    }

    const pivot = tooth.pivot;

    let rotationPoint;

    switch (namePoint) {
        case 'CentralPit':
            rotationPoint = getCentralPitActual(tooth);
            break;
        default:
            rotationPoint = getRotationRootActual(tooth);
    }

    // Définition du point de rotation (rotationRoot) - utilise Vector3 réutilisable
    _tempVector1.set(rotationPoint[0], rotationPoint[1], rotationPoint[2]);
    tooth.updateWorldPosition();

    // Déplacement du pivot sur le point de rotation
    pivot.position.copy(_tempVector1);
    tooth.offsetRefObj();


    // Création du quaternion pour la rotation - utilise Vector3 réutilisable
    _tempVector2.set(rotationAxis[0], rotationAxis[1], rotationAxis[2]);
    const rad = angle * Math.PI / 180;
    const quaternion = new THREE.Quaternion().setFromAxisAngle(_tempVector2, rad);

    // Réalisation de la rotation
    pivot.applyQuaternion(quaternion);
    pivot.updateMatrix();
    matrixUpdateBatcher.add(pivot);


    // Mettre le pivot de nouveau sur le barycentre
    tooth.baryCenter.getWorldPosition(_tempVector3);
    tooth.updateWorldPosition();

    pivot.position.copy(_tempVector3);
    tooth.offsetRefObj();
    matrixUpdateBatcher.add(pivot);


}
